"use client";

import {
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  ExternalLink,
  Layers,
  Loader2,
  Menu,
  Plus,
  RotateCcw,
  Search,
  Sliders,
  Sparkles,
  Tag,
  Trash2,
  X
} from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { AdminPagination } from "@/components/admin/admin-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiPatch } from "@/services/api";

export type CategoryNavRow = {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  showInNav: boolean;
};

export type SpecialLinkConfig = {
  visible: boolean;
  label: string;
  href: string;
};

export type CustomLinkItem = {
  id: string;
  label: string;
  href: string;
  visible: boolean;
};

export type HeaderNavConfigData = {
  showAllButton: boolean;
  showDeals: boolean;
  dealsLabel: string;
  dealsHref: string;
  showPrime: boolean;
  primeLabel: string;
  primeHref: string;
  showCompare: boolean;
  compareLabel: string;
  compareHref: string;
  customLinks: CustomLinkItem[];
};

export function HeaderNavManager({
  initialCategories,
  initialConfig,
  onSaved
}: {
  initialCategories: CategoryNavRow[];
  initialConfig: HeaderNavConfigData;
  onSaved?: (savedConfig: HeaderNavConfigData, savedCategories: CategoryNavRow[]) => void;
}) {
  const [categories, setCategories] = useState<CategoryNavRow[]>(initialCategories);
  const [config, setConfig] = useState<HeaderNavConfigData>(initialConfig);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // New custom link inputs
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkHref, setNewLinkHref] = useState("");

  // Keep state synchronized with parent if updated
  useEffect(() => {
    setCategories(initialCategories);
  }, [initialCategories]);

  useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  const visibleCategories = useMemo(() => {
    return categories
      .filter((c) => c.showInNav)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)
    );
  }, [categories, search]);

  const paginatedCategories = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredCategories.slice(start, start + perPage);
  }, [filteredCategories, page, perPage]);

  function handleDiscard() {
    setCategories(initialCategories);
    setConfig(initialConfig);
    setHasChanges(false);
    toast.info("Unsaved changes discarded.");
  }

  function toggleCategory(id: string) {
    setCategories((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target) return prev;
      const willBeVisible = !target.showInNav;

      let nextOrder = target.sortOrder;
      if (willBeVisible) {
        const currentVisible = prev.filter((c) => c.showInNav);
        const maxOrder =
          currentVisible.length > 0 ? Math.max(...currentVisible.map((c) => c.sortOrder)) : -1;
        nextOrder = maxOrder + 1;
      }

      return prev.map((c) =>
        c.id === id ? { ...c, showInNav: willBeVisible, sortOrder: nextOrder } : c
      );
    });
    setHasChanges(true);
  }

  function updateSortOrder(id: string, sortOrder: number) {
    const cleanOrder = Math.max(0, Math.floor(sortOrder || 0));
    setCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, sortOrder: cleanOrder } : c))
    );
    setHasChanges(true);
  }

  function moveCategory(id: string, direction: "up" | "down") {
    const list = [...visibleCategories];
    const index = list.findIndex((c) => c.id === id);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    const [movedItem] = list.splice(index, 1);
    list.splice(targetIndex, 0, movedItem);

    // Re-index all visible categories sequentially
    const sortOrderMap = new Map(list.map((c, i) => [c.id, i]));

    setCategories((prev) =>
      prev.map((c) => (sortOrderMap.has(c.id) ? { ...c, sortOrder: sortOrderMap.get(c.id)! } : c))
    );
    setHasChanges(true);
  }

  function selectPreset(preset: "first9" | "all" | "none") {
    if (preset === "all") {
      setCategories((prev) =>
        prev.map((c, idx) => ({ ...c, showInNav: true, sortOrder: idx }))
      );
    } else if (preset === "none") {
      setCategories((prev) => prev.map((c) => ({ ...c, showInNav: false })));
    } else if (preset === "first9") {
      setCategories((prev) =>
        prev.map((c, idx) => ({
          ...c,
          showInNav: idx < 9,
          sortOrder: idx
        }))
      );
    }
    setHasChanges(true);
    toast.info(`Applied preset: ${preset === "first9" ? "FIRST 9" : preset.toUpperCase()}`);
  }

  function addCustomLink() {
    if (!newLinkLabel.trim() || !newLinkHref.trim()) {
      toast.error("Label and URL are required.");
      return;
    }
    const newLink: CustomLinkItem = {
      id: `custom-${Date.now()}`,
      label: newLinkLabel.trim(),
      href: newLinkHref.trim(),
      visible: true
    };
    setConfig((prev) => ({
      ...prev,
      customLinks: [...(prev.customLinks || []), newLink]
    }));
    setNewLinkLabel("");
    setNewLinkHref("");
    setHasChanges(true);
    toast.success("Custom link added");
  }

  function updateCustomLink(id: string, updates: Partial<CustomLinkItem>) {
    setConfig((prev) => ({
      ...prev,
      customLinks: (prev.customLinks || []).map((l) => (l.id === id ? { ...l, ...updates } : l))
    }));
    setHasChanges(true);
  }

  function removeCustomLink(id: string) {
    setConfig((prev) => ({
      ...prev,
      customLinks: (prev.customLinks || []).filter((l) => l.id !== id)
    }));
    setHasChanges(true);
  }

  function toggleCustomLink(id: string) {
    setConfig((prev) => ({
      ...prev,
      customLinks: (prev.customLinks || []).map((l) =>
        l.id === id ? { ...l, visible: !l.visible } : l
      )
    }));
    setHasChanges(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        showAllButton: Boolean(config.showAllButton),
        showDeals: Boolean(config.showDeals),
        dealsLabel: config.dealsLabel.trim() || "Today's Deals",
        dealsHref: config.dealsHref.trim() || "/search?deal=flash",
        showPrime: Boolean(config.showPrime),
        primeLabel: config.primeLabel.trim() || "Prime",
        primeHref: config.primeHref.trim() || "/prime",
        showCompare: Boolean(config.showCompare),
        compareLabel: config.compareLabel.trim() || "Compare",
        compareHref: config.compareHref.trim() || "/compare",
        customLinks: (config.customLinks || []).map((l) => ({
          id: l.id,
          label: l.label.trim(),
          href: l.href.trim(),
          visible: Boolean(l.visible)
        })),
        categoryUpdates: categories.map((c) => ({
          id: c.id,
          showInNav: Boolean(c.showInNav),
          sortOrder: Math.max(0, Math.floor(Number(c.sortOrder) || 0))
        }))
      };

      const res = await apiPatch<{
        message?: string;
        config: HeaderNavConfigData;
        categories: CategoryNavRow[];
      }>("/api/admin/nav", payload);

      const updatedConfig: HeaderNavConfigData = res?.config
        ? {
            ...res.config,
            customLinks: Array.isArray(res.config.customLinks) ? res.config.customLinks : []
          }
        : {
            ...config,
            dealsLabel: payload.dealsLabel,
            dealsHref: payload.dealsHref,
            primeLabel: payload.primeLabel,
            primeHref: payload.primeHref,
            compareLabel: payload.compareLabel,
            compareHref: payload.compareHref,
            customLinks: payload.customLinks
          };

      const updatedCategories: CategoryNavRow[] = res?.categories || categories;

      setConfig(updatedConfig);
      setCategories(updatedCategories);
      setHasChanges(false);

      onSaved?.(updatedConfig, updatedCategories);

      toast.success("Storefront header navigation updated successfully!");

      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("nav-updated"));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error saving navigation.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Top Banner & Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <Link href="/admin" className="hover:underline">
              Admin
            </Link>
            <span>/</span>
            <span className="font-bold text-amazon-orange">Header Navigation Bar</span>
          </div>
          <h1 className="mt-1 text-2xl font-black text-slate-950 dark:text-white sm:text-3xl">
            Header Sub-bar Navigation
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Decide exactly which categories and action links appear in the MOVA storefront sub-header
            bar.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              disabled={saving}
              className="text-xs font-bold"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Discard
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 font-bold shadow-md transition-all ${
              hasChanges
                ? "bg-amazon-orange hover:bg-[#e07320] text-slate-950"
                : "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900"
            }`}
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : hasChanges ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
            )}
            {hasChanges ? "Save Changes *" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Real-time Storefront Header Preview */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3 dark:border-white/10 dark:bg-slate-800/50">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amazon-orange" />
            <span className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-200">
              Live Preview of Header Sub-bar (What customers see)
            </span>
          </div>
          <Badge tone="default" className="text-[11px] font-bold">
            {visibleCategories.length} Categories +{" "}
            {(config.showDeals ? 1 : 0) +
              (config.showPrime ? 1 : 0) +
              (config.showCompare ? 1 : 0) +
              (config.customLinks || []).filter((l) => l.visible).length}{" "}
            Links
          </Badge>
        </div>

        <div className="overflow-x-auto bg-amazon-blue p-2.5 text-[13px] text-white sm:p-3">
          <nav className="flex min-w-max items-center gap-1">
            {config.showAllButton && (
              <div className="shadow-xs flex items-center gap-1 rounded-sm border border-white/20 bg-white/10 px-2 py-1 font-bold">
                <Menu className="h-4 w-4" />
                All
              </div>
            )}

            {visibleCategories.length === 0 ? (
              <span className="px-3 text-xs italic text-slate-300">
                (No categories enabled — check categories below to display them here)
              </span>
            ) : (
              visibleCategories.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-1 rounded-sm border border-transparent px-2.5 py-1 transition hover:border-white/40 hover:bg-white/5"
                >
                  <span>{c.name}</span>
                </div>
              ))
            )}

            {config.showDeals && (
              <div className="rounded-sm border border-transparent px-2.5 py-1 font-bold text-amazon-gold hover:border-white/40">
                {config.dealsLabel || "Today's Deals"}
              </div>
            )}

            {config.showPrime && (
              <div className="rounded-sm border border-transparent px-2.5 py-1 font-bold hover:border-white/40">
                {config.primeLabel || "Prime"}
              </div>
            )}

            {config.showCompare && (
              <div className="rounded-sm border border-transparent px-2.5 py-1 hover:border-white/40">
                {config.compareLabel || "Compare"}
              </div>
            )}

            {(config.customLinks || [])
              .filter((l) => l.visible)
              .map((l) => (
                <div
                  key={l.id}
                  className="rounded-sm border border-dashed border-white/30 px-2.5 py-1 text-slate-200 hover:border-white"
                >
                  {l.label}
                </div>
              ))}
          </nav>
        </div>
      </div>

      {/* Special Links Toggle Section */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-black text-slate-950 dark:text-white">
              <Sliders className="h-5 w-5 text-amazon-orange" />
              Special Action Links
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Toggle the visibility, display text, and destination URLs for key sub-header links.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* "All" Button */}
          <div className="rounded-lg border border-slate-200 p-4 transition dark:border-white/10">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm font-bold">
                <Menu className="h-4 w-4 text-slate-500" />
                "☰ All" Menu Button
              </span>
              <button
                type="button"
                onClick={() => {
                  setConfig((c) => ({ ...c, showAllButton: !c.showAllButton }));
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.showAllButton ? "bg-amazon-orange" : "bg-slate-200 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    config.showAllButton ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Opens the category drawer or navigates to all categories.
            </p>
          </div>

          {/* Today's Deals */}
          <div className="rounded-lg border border-slate-200 p-4 transition dark:border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-amazon-gold">Today's Deals</span>
              <button
                type="button"
                onClick={() => {
                  setConfig((c) => ({ ...c, showDeals: !c.showDeals }));
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.showDeals ? "bg-amazon-orange" : "bg-slate-200 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    config.showDeals ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Display Text</label>
                <input
                  type="text"
                  value={config.dealsLabel}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, dealsLabel: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="Today's Deals"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Destination URL</label>
                <input
                  type="text"
                  value={config.dealsHref}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, dealsHref: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="/search?deal=flash"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 font-mono text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
            </div>
          </div>

          {/* Prime */}
          <div className="rounded-lg border border-slate-200 p-4 transition dark:border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">Prime</span>
              <button
                type="button"
                onClick={() => {
                  setConfig((c) => ({ ...c, showPrime: !c.showPrime }));
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.showPrime ? "bg-amazon-orange" : "bg-slate-200 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    config.showPrime ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Display Text</label>
                <input
                  type="text"
                  value={config.primeLabel}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, primeLabel: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="Prime"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Destination URL</label>
                <input
                  type="text"
                  value={config.primeHref}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, primeHref: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="/prime"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 font-mono text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
            </div>
          </div>

          {/* Compare */}
          <div className="rounded-lg border border-slate-200 p-4 transition dark:border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold">Compare</span>
              <button
                type="button"
                onClick={() => {
                  setConfig((c) => ({ ...c, showCompare: !c.showCompare }));
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.showCompare ? "bg-amazon-orange" : "bg-slate-200 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    config.showCompare ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Display Text</label>
                <input
                  type="text"
                  value={config.compareLabel}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, compareLabel: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="Compare"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Destination URL</label>
                <input
                  type="text"
                  value={config.compareHref}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, compareHref: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="/compare"
                  className="mt-0.5 w-full rounded border border-slate-200 bg-transparent px-2.5 py-1 font-mono text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Manager in Header Nav */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-black text-slate-950 dark:text-white">
              <Tag className="h-5 w-5 text-amazon-orange" />
              Category Display & Ordering
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Choose which product categories appear in the header and set their position order.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-bold text-slate-400">Presets:</span>
            <button
              type="button"
              onClick={() => selectPreset("first9")}
              className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
            >
              First 9
            </button>
            <button
              type="button"
              onClick={() => selectPreset("all")}
              className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
            >
              Show All
            </button>
            <button
              type="button"
              onClick={() => selectPreset("none")}
              className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Filter search */}
        <div className="mt-4 flex items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Filter categories..."
              className="w-full rounded-md border border-slate-200 bg-transparent py-1.5 pl-9 pr-3 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
            />
          </div>
          <span className="text-xs font-bold text-slate-400">
            Showing {filteredCategories.length} of {categories.length} categories
          </span>
        </div>

        {/* Category Table */}
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 dark:border-white/10">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
                <tr>
                  <th className="px-4 py-3">Display in Header</th>
                  <th className="px-4 py-3">Category Name</th>
                  <th className="px-4 py-3">Sort Order</th>
                  <th className="px-4 py-3 text-right">Reorder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                {paginatedCategories.map((cat) => {
                  const isVisibleInNav = cat.showInNav;
                  return (
                    <tr
                      key={cat.id}
                      className={`transition-colors ${
                        isVisibleInNav
                          ? "bg-emerald-50/40 hover:bg-emerald-50/70 dark:bg-emerald-950/10 dark:hover:bg-emerald-950/20"
                          : "opacity-75 hover:bg-slate-50 dark:hover:bg-white/5"
                      }`}
                    >
                      {/* Toggle button */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <button
                            type="button"
                            onClick={() => toggleCategory(cat.id)}
                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              isVisibleInNav ? "bg-emerald-600" : "bg-slate-300 dark:bg-slate-700"
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                isVisibleInNav ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <span
                            className={`text-xs font-bold ${
                              isVisibleInNav
                                ? "text-emerald-700 dark:text-emerald-400"
                                : "text-slate-400"
                            }`}
                          >
                            {isVisibleInNav ? "Visible" : "Hidden"}
                          </span>
                        </div>
                      </td>

                      {/* Name & Slug */}
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900 dark:text-white">{cat.name}</div>
                        <div className="font-mono text-[11px] text-slate-400">
                          /search?category={cat.slug}
                        </div>
                      </td>

                      {/* Sort Order */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="0"
                            value={cat.sortOrder}
                            onChange={(e) => updateSortOrder(cat.id, parseInt(e.target.value) || 0)}
                            className="w-16 rounded border border-slate-200 bg-transparent px-2 py-1 font-mono text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10"
                          />
                          <span className="text-[10px] font-medium text-slate-400">
                            (0 = leftmost)
                          </span>
                        </div>
                      </td>

                      {/* Reorder Buttons */}
                      <td className="px-4 py-3 text-right">
                        {isVisibleInNav && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => moveCategory(cat.id, "up")}
                              className="rounded p-1 text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800"
                              title="Move Left / Earlier"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveCategory(cat.id, "down")}
                              className="rounded p-1 text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800"
                              title="Move Right / Later"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <AdminPagination
          currentPage={page}
          totalItems={filteredCategories.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          itemLabel="categories"
        />
      </div>

      {/* Custom Links Section */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
        <h2 className="flex items-center gap-2 text-lg font-black text-slate-950 dark:text-white">
          <Layers className="h-5 w-5 text-amazon-orange" />
          Custom Header Links
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Add any custom promotional or service links directly to the sub-header (e.g. "Customer
          Service", "Gift Cards", "Sell").
        </p>

        {/* Existing custom links */}
        {(config.customLinks || []).length > 0 && (
          <div className="mt-4 space-y-3">
            {config.customLinks.map((link) => (
              <div
                key={link.id}
                className="flex flex-col gap-3 rounded-lg border border-slate-200 p-3 text-sm dark:border-white/10 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex flex-1 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleCustomLink(link.id)}
                    className={`shrink-0 rounded px-2.5 py-1 text-xs font-bold transition ${
                      link.visible
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400"
                        : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {link.visible ? "Visible" : "Hidden"}
                  </button>
                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateCustomLink(link.id, { label: e.target.value })}
                      placeholder="Link Label"
                      className="rounded border border-slate-200 bg-transparent px-2.5 py-1 text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10"
                    />
                    <input
                      type="text"
                      value={link.href}
                      onChange={(e) => updateCustomLink(link.id, { href: e.target.value })}
                      placeholder="/destination-url"
                      className="rounded border border-slate-200 bg-transparent px-2.5 py-1 font-mono text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeCustomLink(link.id)}
                  className="self-end p-1 text-red-500 hover:text-red-700 sm:self-center"
                  title="Remove Link"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add link form */}
        <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 dark:border-white/10 sm:flex-row">
          <input
            type="text"
            value={newLinkLabel}
            onChange={(e) => setNewLinkLabel(e.target.value)}
            placeholder="Link Label (e.g. Customer Service)"
            className="flex-1 rounded-md border border-slate-200 bg-transparent px-3 py-2 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <input
            type="text"
            value={newLinkHref}
            onChange={(e) => setNewLinkHref(e.target.value)}
            placeholder="Link URL (e.g. /help or /gift-cards)"
            className="flex-1 rounded-md border border-slate-200 bg-transparent px-3 py-2 font-mono text-xs outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <Button
            type="button"
            onClick={addCustomLink}
            variant="outline"
            className="shrink-0 text-xs font-bold"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Link
          </Button>
        </div>
      </div>

      {/* Floating Save Bar when changes exist */}
      {hasChanges && (
        <div className="fixed bottom-4 left-4 right-4 z-50 flex max-w-[calc(100vw-2rem)] flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/20 bg-amazon-navy px-4 py-3 text-white shadow-2xl sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-md sm:flex-nowrap sm:px-5 sm:py-3.5">
          <span className="min-w-0 flex-1 text-xs font-bold text-amazon-gold">
            You have unsaved changes to the header navigation!
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              disabled={saving}
              className="border-white/20 text-xs font-bold text-slate-200 hover:bg-white/10"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Discard
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="shrink-0 rounded-xl bg-amazon-orange px-4 py-2 text-xs font-black text-slate-950 hover:bg-[#e07320]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Now"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
